import * as Yup from "yup";
import striptags from "striptags";

export const getEmailsFromString = (emails: string | undefined) =>
  emails
    ? emails
        .split(",")
        .map((email: string) => email.trim())
        .filter((email) => email)
    : [];

const emailSchema = Yup.string().email("Invalid email");

export const yupEmailListSchema = Yup.string().test(
  "test-comma-separated-emails",
  "One or more emails are invalid or the list is empty.",
  // @ts-ignore
  (emailsString: string) => {
    const emails = getEmailsFromString(emailsString);

    return (
      emails.length > 0 &&
      emails.every((email: string) => emailSchema.isValidSync(email as string))
    );
  }
);

/**
 * Check if htmlBody is empty or if its plain text is empty (after stripping HTML tags)
 */
export const isEmailHTMLBodyEmpty = (htmlBody?: string | null) => {
  return !htmlBody || striptags(htmlBody || "").trim() === "";
};

const NBSP = "&nbsp;";

/**
 * The editor renders with white-space: pre-wrap, so a run of spaces typed into
 * a draft is visible while composing. Email clients render the sent HTML
 * without pre-wrap, and the HTML spec collapses any run of whitespace down to
 * a single space, so the recipient saw one space where the SDR typed two.
 *
 * Convert each run of two or more spaces into alternating &nbsp; and plain
 * spaces (the same approach Gmail uses). Alternating rather than emitting a
 * solid block of &nbsp; keeps the line breakable, so long lines still wrap.
 *
 * Only text between tags is touched. Spaces inside a tag are structural
 * (attribute separators, href values) and must survive untouched.
 */
export const preserveEmailHTMLSpacing = (html: string) =>
  html.replace(/(<[^>]+>)|([^<]+)/g, (_match, tag?: string, text?: string) => {
    if (tag) {
      return tag;
    }

    return (text || "").replace(/ {2,}/g, (run) =>
      Array.from(run, (_char, i) => (i % 2 === 0 ? NBSP : " ")).join("")
    );
  });

/**
 * A `<br>` that is the last thing inside a block, and the blocks it belongs to.
 *
 * `attrs` keeps whatever was on the opening tag, `inner` is everything before
 * the trailing run, `breaks` is that run. `inner` is tempered against
 * `</div>` so the match cannot span two sibling blocks.
 */
const BLOCK_WITH_TRAILING_BREAKS =
  /<div([^>]*)>((?:(?!<\/div>)[\s\S])*?)((?:<br\s*\/?>\s*)+)<\/div>/gi;

const SINGLE_BREAK = /<br\s*\/?>/gi;

/**
 * Turn a block-trailing `<br>` into a spacer block of its own.
 *
 * A `<br>` at the very end of a block is not rendered by anything that lays
 * out HTML: the block already ends the line, so the break has nothing left to
 * break. ProseMirror works around that with its own trailing-break element so
 * the line stays visible while editing, which is why `<div>text<br></div>`
 * shows a blank line underneath in the editor and none at all in the
 * recipient's client. Paragraph spacing therefore survived the draft and
 * vanished on send - consistently, and only in the body: the signature is
 * built server-side out of real `<div><br></div>` spacers, so its blank lines
 * came through and the body's did not.
 *
 * Promoting the break to `<div><br></div>` makes the sent mail match the
 * draft. A block whose content is only breaks is already a spacer and is left
 * exactly as it is - rewriting those would add a blank line to every
 * signature.
 */
export const promoteTrailingBreaksToSpacers = (html: string) =>
  html.replace(
    BLOCK_WITH_TRAILING_BREAKS,
    (match, attrs: string, inner: string, breaks: string) => {
      if (!inner.trim()) {
        return match;
      }

      const spacers = (breaks.match(SINGLE_BREAK) || []).length;

      return `<div${attrs}>${inner}</div>${"<div><br></div>".repeat(spacers)}`;
    }
  );

/**
 * Final transform applied to an email body immediately before it is sent.
 *
 * This is the single boundary where draft HTML becomes wire HTML. It is
 * deliberately not applied inside the editor's onChange: the editor feeds its
 * own output back in through the `content` prop, so transforming there would
 * rewrite the document on every keystroke.
 */
export const prepareEmailHTMLForSend = (htmlBody?: string | null) => {
  if (!htmlBody) {
    return "";
  }

  return preserveEmailHTMLSpacing(promoteTrailingBreaksToSpacers(htmlBody));
};
